import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { searchChannels, setSearchQuery, clearSearchResults } from '../channelsSlice';

interface UseChannelSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  maxResults?: number;
}

interface UseChannelSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  isSearching: boolean;
  searchResults: any[];
  searchError: string | null;
  hasSearched: boolean;
  clearResults: () => void;
  triggerSearch: (searchQuery?: string) => void;
}

export const useChannelSearch = (
  options: UseChannelSearchOptions = {}
): UseChannelSearchReturn => {
  const {
    debounceMs = 300,
    minQueryLength = 2,
    maxResults = 25,
  } = options;

  const dispatch = useAppDispatch();
  const { 
    searchResults, 
    isSearching, 
    searchError, 
    searchQuery,
    hasSearched 
  } = useAppSelector(state => state.channels);

  const [inputValue, setInputValue] = useState(searchQuery || '');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue.trim().length >= minQueryLength && inputValue.trim() !== searchQuery) {
        dispatch(searchChannels({ 
          query: inputValue.trim(), 
          maxResults 
        }));
      } else if (inputValue.trim().length < minQueryLength && searchResults.length > 0) {
        dispatch(clearSearchResults());
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [inputValue, minQueryLength, maxResults, debounceMs, dispatch, searchQuery, searchResults.length]);

  useEffect(() => {
    dispatch(setSearchQuery(inputValue));
  }, [inputValue, dispatch]);

  const setQuery = useCallback((query: string) => {
    setInputValue(query);
  }, []);

  const clearResults = useCallback(() => {
    dispatch(clearSearchResults());
    setInputValue('');
  }, [dispatch]);

  const triggerSearch = useCallback((searchQuery?: string) => {
    const queryToSearch = searchQuery || inputValue.trim();
    if (queryToSearch.length >= minQueryLength) {
      dispatch(searchChannels({ 
        query: queryToSearch, 
        maxResults 
      }));
    }
  }, [inputValue, minQueryLength, maxResults, dispatch]);

  return {
    query: inputValue,
    setQuery,
    isSearching,
    searchResults,
    searchError,
    hasSearched,
    clearResults,
    triggerSearch,
  };
};
